import React, { useState } from 'react';
import { Box, Typography, Card, CardActionArea, CardContent, Stack, Chip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { GUIDES } from '../content/guides';
import { GUIDE_CATEGORIES, GuideCategory } from '../types/guide';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const CATEGORY_BLURBS: Record<GuideCategory, string> = {
  'Getting started': 'A quick tour of every tool on the site.',
  Converter: 'Turn PDF, Word and Excel files into text or Markdown.',
  'PDF tools': 'Merge, split, reorder, flatten and export PDF pages.',
  'Text editor': 'Clean up, convert, search and export text.',
  Typing: 'Measure and improve your typing speed.',
  'File formats': 'Choosing the right format for the job.',
};

const GuidesIndexPage: React.FC = () => {
  useDocumentMeta({
    title: 'Guides, Tips & Tricks for Every Tool | PDF-To-Text.com',
    description:
      'Tips and how-tos for every tool: converting PDF, Word and Excel files, PDF page tools, the online text editor, PDF export and improving your typing speed.',
    canonicalPath: '/guides',
  });
  const [filter, setFilter] = useState<GuideCategory | 'All'>('All');

  const categories = GUIDE_CATEGORIES.filter((c) => GUIDES.some((g) => g.category === c));
  const shown = filter === 'All' ? categories : [filter];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>Guides, tips &amp; tricks</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Practical how-tos for every tool on the site — all free, private and in your browser.
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        {(['All', ...categories] as const).map((c) => (
          <Chip
            key={c}
            label={c === 'All' ? `All (${GUIDES.length})` : c}
            color={filter === c ? 'primary' : 'default'}
            variant={filter === c ? 'filled' : 'outlined'}
            onClick={() => setFilter(c)}
          />
        ))}
      </Stack>

      <Stack spacing={4}>
        {shown.map((category) => (
          <Box key={category} component="section">
            <Typography variant="h6" component="h2">{category}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{CATEGORY_BLURBS[category]}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              {GUIDES.filter((g) => g.category === category).map((guide) => (
                <Card key={guide.slug} variant="outlined">
                  <CardActionArea component={RouterLink} to={`/guides/${guide.slug}`} sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{guide.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {guide.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default GuidesIndexPage;
